import React, { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import MobileNav from '@/components/MobileNav';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Download, Calendar, FileSpreadsheet } from 'lucide-react';
import api, { getEvents } from '@/lib/api';
import { toast } from 'sonner';

const COLORS = ['#2E5C55', '#D4E4BC', '#4A7C74', '#8FA68C', '#B8C9A4'];

const Reports = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadMonths();
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await getEvents();
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEvent(response.data[0].event_id);
      }
    } catch (error) {
      console.error('Failed to load events');
    }
  };

  useEffect(() => {
    if (selectedMonth) {
      loadReport();
    }
  }, [selectedMonth]);

  const loadMonths = async () => {
    try {
      const response = await api.get('/reports/months');
      setAvailableMonths(response.data.months);
      if (response.data.months.length > 0) {
        setSelectedMonth(response.data.months[0]);
      } else {
        // No months available, stop loading
        setLoading(false);
      }
    } catch (error) {
      toast.error('Failed to load months');
      setLoading(false);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/reports/monthly/${selectedMonth}`);
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    if (!reportData) return;
    
    const csvContent = generateCSV(reportData);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ISM_Report_${selectedMonth}.csv`;
    a.click();
    toast.success('Report downloaded!');
  };

  const downloadExcelReport = async () => {
    if (!selectedEvent) {
      toast.error('Please select an event');
      return;
    }
    
    setExporting(true);
    try {
      const response = await api.get(`/reports/export-excel/${selectedEvent}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'ISM_Financial_Report.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel report downloaded!');
    } catch (error) {
      toast.error('Failed to download Excel report');
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const generateCSV = (data) => {
    let csv = 'India Saved Mission - Monthly Report\n';
    csv += `Month: ${selectedMonth}\n\n`;
    csv += `Total Expenses: ${data.total_inr} INR + ${data.total_usd} USD\n\n`;
    csv += 'Category,Amount (INR)\n';
    data.by_category.forEach(item => {
      csv += `${item.category},${item.amount}\n`;
    });
    csv += '\nEvent,Amount (INR)\n';
    data.by_event.forEach(item => {
      csv += `${item.event_name},${item.amount}\n`;
    });
    return csv;
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col overflow-hidden md:ml-64">
        <header className="bg-white border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>Monthly Reports</h2>
              <p className="text-sm text-muted-foreground">Expense summaries and breakdowns</p>
            </div>
            <div className="flex items-center space-x-2">
              <Button onClick={downloadReport} disabled={!reportData} variant="outline" data-testid="download-csv-button">
                <Download className="h-4 w-4 mr-2" />
                CSV
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 pb-24 md:pb-6">
          {loading && !reportData ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="max-w-7xl mx-auto space-y-6">
              {/* Full Excel Report Download */}
              <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-accent/10">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <FileSpreadsheet className="h-5 w-5 mr-2 text-primary" />
                    Download Full Event Report
                  </CardTitle>
                  <CardDescription>
                    Download a complete Excel report with all departments, transactions, and income details
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="flex-1 w-full sm:w-auto">
                      <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                        <SelectTrigger className="w-full sm:w-72" data-testid="event-select-excel">
                          <SelectValue placeholder="Select an event" />
                        </SelectTrigger>
                        <SelectContent>
                          {events.map((event) => (
                            <SelectItem key={event.event_id} value={event.event_id}>
                              {event.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      onClick={downloadExcelReport} 
                      disabled={!selectedEvent || exporting}
                      className="w-full sm:w-auto"
                      data-testid="download-excel-button"
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      {exporting ? 'Exporting...' : 'Download Excel Report'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Month Selector */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-4">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <Label className="text-sm font-medium">Select Month:</Label>
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger className="w-64" data-testid="month-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableMonths.map((month) => (
                          <SelectItem key={month} value={month}>
                            {formatMonth(month)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {reportData && (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="bg-gradient-to-br from-primary/10 to-accent/20">
                      <CardContent className="p-6">
                        <p className="text-sm text-muted-foreground mb-1">Total Spent (INR)</p>
                        <p className="text-3xl font-bold text-primary">₹{reportData.total_inr.toLocaleString()}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-accent/20 to-primary/10">
                      <CardContent className="p-6">
                        <p className="text-sm text-muted-foreground mb-1">Total Spent (USD)</p>
                        <p className="text-3xl font-bold text-primary">${reportData.total_usd.toLocaleString()}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-primary/10 to-accent/20">
                      <CardContent className="p-6">
                        <p className="text-sm text-muted-foreground mb-1">Total Transactions</p>
                        <p className="text-3xl font-bold text-primary">{reportData.total_count}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Category Breakdown - Pie Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle>By Category</CardTitle>
                        <CardDescription>Where the money went</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={reportData.by_category}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ category, percent }) => `${category} (${(percent * 100).toFixed(0)}%)`}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="amount"
                            >
                              {reportData.by_category.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Event Breakdown - Bar Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle>By Event</CardTitle>
                        <CardDescription>Expenses per revival meeting</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={reportData.by_event}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="event_name" angle={-45} textAnchor="end" height={100} />
                            <YAxis />
                            <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                            <Bar dataKey="amount" fill="#2E5C55" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Detailed Tables */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Category Details</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {reportData.by_category.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-accent/20 rounded-lg">
                              <div className="flex items-center space-x-3">
                                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                                <span className="font-medium">{item.category}</span>
                              </div>
                              <span className="font-bold text-primary">₹{item.amount.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Event Details</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {reportData.by_event.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-accent/20 rounded-lg">
                              <span className="font-medium">{item.event_name}</span>
                              <span className="font-bold text-primary">₹{item.amount.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}
            </div>
          )}
        </main>

        <MobileNav user={user} />
      </div>
    </div>
  );
};

export default Reports;
